import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './LunchRouletteOverview.module.css';
import { lunchRouletteApi, LunchRouletteOverview, LunchRoulette } from '../../services/lunchRouletteApi';
import { useUserContext } from '../../contexts/UserContext';

// Bootstrap Modal interface
interface BootstrapModal {
  show: () => void;
  hide: () => void;
}

declare global {
  interface Window {
    bootstrap: {
      Modal: {
        getInstance: (element: Element) => BootstrapModal | null;
        new (element: Element): BootstrapModal;
      };
    };
  }
}



const LunchRouletteOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUserContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<LunchRouletteOverview | null>(null);
  const [showPastEvents, setShowPastEvents] = useState<boolean>(false);
  const [selectedRouletteId, setSelectedRouletteId] = useState<string | null>(null);
  const [unregisterRouletteId, setUnregisterRouletteId] = useState<string | null>(null);
  const [registrationComment, setRegistrationComment] = useState<string>('');
  const [registering, setRegistering] = useState<boolean>(false);
  const [unregistering, setUnregistering] = useState<boolean>(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    group_size: 2,
    registration_start: '',
    registration_end: '',
    lunch_date: '',
    is_hybrid: false,
    not_same_department: false,
    not_same_employee_group: false
  });

  // Helper function to extract error message
  const getErrorMessage = (error: any): string => {
    if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Check if user has admin permissions
  const isAdmin = (): boolean => {
    return data?.user_permissions?.some(role => 
      ['Admin', 'Lunch-Roulette Admin'].includes(role)
    ) || false;
  };

  // Helper function to get registered count for a roulette
  const getRegisteredCount = (rouletteId: string): number => {
    if (!data?.all_registrations) return 0;
    return data.all_registrations.filter(reg => reg.Roulette_ID === rouletteId).length;
  };

  // Fetch lunch roulette overview
  const fetchOverview = async () => {
    try {
      setLoading(true);
      const overview = await lunchRouletteApi.getOverview();
      setData(overview);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to load lunch roulette data: ${errorMessage}`);
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle registration
  const handleRegister = async () => {
    if (!selectedRouletteId) return;

    try {
      setRegistering(true);
      const response = await lunchRouletteApi.register(selectedRouletteId, registrationComment);
      toast.success(response.message);
      
      // Close modal and cleanup
      cleanupModalBackdrops();
      
      setRegistrationComment('');
      setSelectedRouletteId(null);
      await fetchOverview(); // Refresh data
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Registration failed: ${errorMessage}`);
    } finally {
      setRegistering(false);
    }
  };

  // Handle unregistration
  const handleUnregister = async () => {
    if (!unregisterRouletteId) return;

    try {
      setUnregistering(true);
      const response = await lunchRouletteApi.unregister(unregisterRouletteId);
      toast.success(response.message);
      
      // Close modal and cleanup
      cleanupModalBackdrops();
      
      setUnregisterRouletteId(null);
      await fetchOverview(); // Refresh data
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Unregistration failed: ${errorMessage}`);
    } finally {
      setUnregistering(false);
    }
  };

  // Open unregister confirmation modal
  const openUnregisterModal = (rouletteId: string) => {
    setUnregisterRouletteId(rouletteId);
    
    // Use a timeout to ensure state is set before opening modal
    setTimeout(() => {
      const modal = document.getElementById('unregisterModal');
      if (modal) {
        try {
          if (window.bootstrap && window.bootstrap.Modal) {
            const modalInstance = window.bootstrap.Modal.getInstance(modal) || new window.bootstrap.Modal(modal);
            modalInstance.show();
          } else {
            // Fallback approaches
            const bootstrapModal = (modal as any);
            if (bootstrapModal.modal) {
              bootstrapModal.modal('show');
            } else {
              // Direct manipulation
              modal.style.display = 'block';
              modal.classList.add('show');
              modal.setAttribute('aria-hidden', 'false');
              document.body.classList.add('modal-open');
              
              // Add backdrop
              const backdrop = document.createElement('div');
              backdrop.className = 'modal-backdrop fade show';
              backdrop.id = 'unregister-modal-backdrop';
              document.body.appendChild(backdrop);
            }
          }
        } catch (error) {
          console.error('Error opening unregister modal:', error);
        }
      }
    }, 10);
  };

  // Handle create roulette
  const handleCreateRoulette = async () => {
    try {
      const response = await lunchRouletteApi.create(createForm);
      toast.success(response.message);
      
      // Close modal and cleanup
      const modal = document.getElementById('lunchRouletteModal');
      if (modal && window.bootstrap) {
        const modalInstance = window.bootstrap.Modal.getInstance(modal) || new window.bootstrap.Modal(modal);
        modalInstance.hide();
      }
      
      // Cleanup any lingering backdrops
      setTimeout(cleanupModalBackdrops, 300);
      
      setCreateForm({
        group_size: 2,
        registration_start: '',
        registration_end: '',
        lunch_date: '',
        is_hybrid: false,
        not_same_department: false,
        not_same_employee_group: false
      });
      await fetchOverview(); // Refresh data
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to create lunch roulette: ${errorMessage}`);
    }
  };

  // Open registration modal
  const openRegisterModal = (rouletteId: string) => {
    console.log('openRegisterModal called with ID:', rouletteId);
    setSelectedRouletteId(rouletteId);
    
    // Use a timeout to ensure state is set before opening modal
    setTimeout(() => {
      console.log('Timeout triggered, attempting to show modal');
      const success = showModalWithDebug('registerModal');
      if (!success) {
        console.error('Failed to open registration modal');
        toast.error('Failed to open registration modal. Please try again.');
      }
    }, 10);
  };

  // Helper function to show modal with debug logging
  const showModalWithDebug = (modalId: string) => {
    console.log(`Attempting to open modal: ${modalId}`);
    const modal = document.getElementById(modalId);
    
    if (!modal) {
      console.error(`Modal element not found: ${modalId}`);
      return false;
    }
    
    console.log('Modal element found:', modal);
    console.log('window.bootstrap available:', !!window.bootstrap);
    
    // Method 1: Try Bootstrap JavaScript API
    if (window.bootstrap && window.bootstrap.Modal) {
      try {
        console.log('Trying Bootstrap Modal API...');
        const modalInstance = window.bootstrap.Modal.getInstance(modal) || new window.bootstrap.Modal(modal);
        modalInstance.show();
        console.log('Bootstrap Modal API succeeded');
        return true;
      } catch (error) {
        console.warn('Bootstrap Modal API failed:', error);
      }
    }

    // Method 2: Manual DOM manipulation
    try {
      console.log('Trying manual DOM manipulation...');
      modal.style.display = 'block';
      modal.classList.add('show');
      modal.setAttribute('aria-hidden', 'false');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('role', 'dialog');
      document.body.classList.add('modal-open');
      
      // Add backdrop if it doesn't exist
      const existingBackdrop = document.querySelector(`#${modalId}-backdrop`);
      if (!existingBackdrop) {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        backdrop.id = `${modalId}-backdrop`;
        document.body.appendChild(backdrop);
        
        // Add click handler to backdrop to close modal
        backdrop.addEventListener('click', () => {
          console.log('Backdrop clicked, closing modal');
          cleanupModalBackdrops();
          if (modalId === 'registerModal') {
            setRegistrationComment('');
            setSelectedRouletteId(null);
          } else if (modalId === 'unregisterModal') {
            setUnregisterRouletteId(null);
          }
        });
      }
      
      console.log('Manual DOM manipulation succeeded');
      return true;
    } catch (error) {
      console.error('Manual DOM manipulation failed:', error);
      return false;
    }
  };

  // Handle URL parameters for auto-opening registration modal
  const handleUrlParameters = () => {
    const openRegisterParam = searchParams.get('open_register');
    if (openRegisterParam && data) {
      const rouletteId = openRegisterParam.trim();

      if (!rouletteId) {
        toast.error('Invalid lunch roulette ID in URL parameter.');
        searchParams.delete('open_register');
        setSearchParams(searchParams, { replace: true });
        return;
      }

      // Check if the roulette exists in upcoming events
      const roulette = data.upcoming?.find(r => r.Roulette_ID === rouletteId);
      
      if (roulette) {
        // Check if registration is open and user is not already registered
        if (roulette.registration_status === 'open' && !roulette.is_registered) {
          openRegisterModal(rouletteId);
          // Remove the parameter from URL after opening modal
          searchParams.delete('open_register');
          setSearchParams(searchParams, { replace: true });
        } else if (roulette.is_registered) {
          toast.info('You are already registered for this lunch roulette.');
          searchParams.delete('open_register');
          setSearchParams(searchParams, { replace: true });
        } else if (roulette.registration_status === 'closed') {
          toast.warning('Registration for this lunch roulette is closed.');
          searchParams.delete('open_register');
          setSearchParams(searchParams, { replace: true });
        } else if (roulette.registration_status === 'not_started') {
          toast.info('Registration for this lunch roulette has not started yet.');
          searchParams.delete('open_register');
          setSearchParams(searchParams, { replace: true });
        }
      } else {
        toast.error('Lunch roulette not found or no longer available.');
        searchParams.delete('open_register');
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  // Navigate to admin panel
  const navigateToAdminPanel = (rouletteId: string) => {
    navigate(`/lunch-roulette/admin/${rouletteId}`);
  };

  // Check if user can see matches
  const canSeeMatches = (roulette: LunchRoulette): boolean => {
    const now = new Date();
    const registrationEnd = new Date(roulette.registration_end);
    // Show matches if user is registered and registration has ended
    // This allows users to see their matches once registration closes
    return roulette.is_registered && now >= registrationEnd;
  };

  type StatusVariant = LunchRoulette['registration_status'] | 'completed';

  const getStatusLabel = (status: StatusVariant): string => {
    switch (status) {
      case 'open':
        return 'Registration Open';
      case 'closed':
        return 'Registration Closed';
      case 'not_started':
        return 'Registration Pending';
      case 'completed':
        return 'Completed';
      default:
        return 'Status Unknown';
    }
  };

  const getStatusClass = (status: StatusVariant): string => {
    switch (status) {
      case 'open':
        return styles.statusOpen;
      case 'closed':
        return styles.statusClosed;
      case 'not_started':
        return styles.statusPending;
      case 'completed':
        return styles.statusCompleted;
      default:
        return styles.statusPending;
    }
  };

  // Helper function to clean up modal backdrops
  const cleanupModalBackdrops = () => {
    // Remove any lingering modal backdrops
    const backdrops = document.querySelectorAll('.modal-backdrop, #register-modal-backdrop, #unregister-modal-backdrop');
    backdrops.forEach(backdrop => backdrop.remove());
    
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
    
    // Reset body style
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    
    // Close any manually opened modals
    const modals = document.querySelectorAll('.modal.show');
    modals.forEach(modal => {
      modal.classList.remove('show');
      (modal as HTMLElement).style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
    });
  };

  useEffect(() => {
    fetchOverview();
    
    // Add event listeners for modal cleanup
    const handleModalHidden = () => {
      setTimeout(cleanupModalBackdrops, 100);
    };
    
    // Handle modal show events for unregister modal
    const handleUnregisterModalShow = (event: any) => {
      const button = event.relatedTarget;
      if (button) {
        const rouletteCard = button.closest('[data-roulette-id]');
        if (rouletteCard) {
          const rouletteId = rouletteCard.getAttribute('data-roulette-id');
          if (rouletteId && !unregisterRouletteId) {
            setUnregisterRouletteId(rouletteId);
          }
        }
      }
    };
    
    const registerModal = document.getElementById('registerModal');
    const unregisterModal = document.getElementById('unregisterModal');
    const createModal = document.getElementById('lunchRouletteModal');
    
    if (registerModal) {
      registerModal.addEventListener('hidden.bs.modal', handleModalHidden);
    }
    if (unregisterModal) {
      unregisterModal.addEventListener('hidden.bs.modal', handleModalHidden);
      unregisterModal.addEventListener('show.bs.modal', handleUnregisterModalShow);
    }
    if (createModal) {
      createModal.addEventListener('hidden.bs.modal', handleModalHidden);
    }
    
    // Cleanup function to remove event listeners and backdrops when component unmounts
    return () => {
      if (registerModal) {
        registerModal.removeEventListener('hidden.bs.modal', handleModalHidden);
      }
      if (unregisterModal) {
        unregisterModal.removeEventListener('hidden.bs.modal', handleModalHidden);
        unregisterModal.removeEventListener('show.bs.modal', handleUnregisterModalShow);
      }
      if (createModal) {
        createModal.removeEventListener('hidden.bs.modal', handleModalHidden);
      }
      cleanupModalBackdrops();
    };
  }, []);

  // Handle URL parameters when data is loaded
  useEffect(() => {
    if (data && !loading) {
      handleUrlParameters();
    }
  }, [data, loading, searchParams]);

  if (loading) {
    return (
      <div className={`container ${styles.loadingContainer}`}>
        <div className={`spinner-border text-primary ${styles.loadingSpinner}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3>Loading lunch roulettes...</h3>
      </div>
    );
  }

  const hasAdminRights = isAdmin();
  const upcomingCount = data?.upcoming?.length ?? 0;
  const pastCount = data?.past?.length ?? 0;
  const participantCount = data?.all_registrations?.length ?? 0;
  const nextRoulette = data?.upcoming?.[0];
  const nextRegistrationWindow = nextRoulette
    ? `${formatDate(nextRoulette.registration_start)} - ${formatDate(nextRoulette.registration_end)}`
    : 'No registration window is currently open.';

  return (
    <div className={`container ${styles.pageShell}`}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Lunch Roulette</h1>
          <p className={styles.pageSubtitle}>
            Meet colleagues across offices and teams for an informal lunch exchange.
          </p>
          {nextRoulette && (
            <p className={styles.pageInfo}>
              Current registration window: {nextRegistrationWindow}
            </p>
          )}
        </div>
        {hasAdminRights && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.primaryAction}`}
            data-bs-toggle="modal"
            data-bs-target="#lunchRouletteModal"
          >
            <i className="bi bi-plus-lg"></i>
            Create Lunch Roulette
          </button>
        )}
      </header>

      <section className={styles.sectionBlock}>
        <header className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionHeading}>Upcoming Lunch Roulettes</h2>
            <p className={styles.sectionSubheading}>
              {upcomingCount > 0
                ? 'Secure your seat and get matched for a shared lunch.'
                : 'No lunch roulette is currently scheduled.'}
            </p>
          </div>

          <div className={styles.sectionToolbar}>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.ghostAction} ${showPastEvents ? styles.ghostActionActive : ''}`}
              onClick={() => setShowPastEvents(!showPastEvents)}
            >
              <i className="bi bi-clock-history"></i>
              {showPastEvents ? 'Hide past roulettes' : 'Show past roulettes'}
            </button>
          </div>
        </header>

        {upcomingCount > 0 ? (
          <div className={styles.cardGrid}>
            {data?.upcoming?.map((roulette) => {
              const statusLabel = getStatusLabel(roulette.registration_status);
              const statusClass = getStatusClass(roulette.registration_status);
              const registrationWindow = `${formatDate(roulette.registration_start)} - ${formatDate(roulette.registration_end)}`;
              const formatIconClass = roulette.is_hybrid ? styles.iconGreen : styles.iconRed;

              return (
                <article key={roulette.Roulette_ID} className={styles.rouletteCard} data-roulette-id={roulette.Roulette_ID}>
                  <header className={styles.cardHeader}>
                    <div className={styles.cardHeaderLeft}>
                      <span className={styles.cardIcon}>
                        <i className="bi bi-cup-hot-fill"></i>
                      </span>
                      <div>
                        <h3 className={styles.cardTitle}>Lunch Roulette</h3>
                        <p className={styles.cardSubline}>{registrationWindow}</p>
                      </div>
                    </div>

                    <span className={`${styles.statusPill} ${statusClass}`}>{statusLabel}</span>
                  </header>

                  <div className={styles.cardBody}>
                    <div className={styles.detailGrid}>
                      <div className={styles.detailRow}>
                        <span className={`${styles.detailIcon} ${styles.iconBlue}`}>
                          <i className="bi bi-people-fill"></i>
                        </span>
                        <div>
                          <p className={styles.detailLabel}>Group Size</p>
                          <p className={styles.detailValue}>{roulette.group_size} people</p>
                        </div>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={`${styles.detailIcon} ${styles.iconGold}`}>
                          <i className="bi bi-calendar-event-fill"></i>
                        </span>
                        <div>
                          <p className={styles.detailLabel}>Registration Window</p>
                          <p className={styles.detailValue}>{registrationWindow}</p>
                        </div>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={`${styles.detailIcon} ${formatIconClass}`}>
                          <i className={`bi ${roulette.is_hybrid ? 'bi-camera-video-fill' : 'bi-geo-alt-fill'}`}></i>
                        </span>
                        <div>
                          <p className={styles.detailLabel}>Format</p>
                          <p className={styles.detailValue}>{roulette.is_hybrid ? 'Hybrid (Microsoft Teams)' : 'In-person event'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {hasAdminRights && (
                    <section className={styles.adminInfoBox}>
                      <div className={styles.adminGrid}>
                        <div className={styles.adminItem}>
                          <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                            <i className="bi bi-calendar-check-fill"></i>
                          </span>
                          <div>
                            <p className={styles.adminLabel}>Lunch date</p>
                            <p className={styles.adminValue}>{formatDate(roulette.lunch_date)}</p>
                          </div>
                        </div>

                        <div className={styles.adminItem}>
                          <span className={`${styles.adminIcon} ${styles.iconGreen}`}>
                            <i className="bi bi-person-check-fill"></i>
                          </span>
                          <div>
                            <p className={styles.adminLabel}>Registered users</p>
                            <p className={styles.adminValue}>{getRegisteredCount(roulette.Roulette_ID)}</p>
                          </div>
                        </div>

                        <div className={styles.adminItem}>
                          <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                            <i className="bi bi-building"></i>
                          </span>
                          <div>
                            <p className={styles.adminLabel}>Avoid same dept.</p>
                            <p className={styles.adminValue}>{roulette.not_same_department ? 'Yes' : 'No'}</p>
                          </div>
                        </div>

                        <div className={styles.adminItem}>
                          <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                            <i className="bi bi-diagram-3-fill"></i>
                          </span>
                          <div>
                            <p className={styles.adminLabel}>Avoid same group</p>
                            <p className={styles.adminValue}>{roulette.not_same_employee_group ? 'Yes' : 'No'}</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  <footer className={styles.cardActions}>
                    <div className={styles.actionGroup}>
                      {roulette.registration_status === 'open' && (
                        roulette.is_registered ? (
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.ghostAction}`}
                            onClick={() => openUnregisterModal(roulette.Roulette_ID)}
                            disabled={unregistering || roulette.has_matches}
                            title={roulette.has_matches ? 'Cannot unregister - matches have already been created' : 'Unregister from this roulette'}
                          >
                            <i className="bi bi-x-circle-fill"></i>
                            Unregister
                            {roulette.has_matches && <i className={`bi bi-lock-fill ${styles.lockIcon}`}></i>}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.primaryAction}`}
                            onClick={() => openRegisterModal(roulette.Roulette_ID)}
                          >
                            <i className="bi bi-check-circle-fill"></i>
                            Register now
                          </button>
                        )
                      )}

                      {roulette.registration_status === 'not_started' && (
                        <button type="button" className={`${styles.actionButton} ${styles.disabledAction}`} disabled>
                          <i className="bi bi-clock-fill"></i>
                          Registration not started
                        </button>
                      )}

                      {roulette.registration_status === 'closed' && roulette.is_registered && (
                        <button type="button" className={`${styles.actionButton} ${styles.disabledAction}`} disabled>
                          <i className="bi bi-check-circle-fill"></i>
                          You are registered
                        </button>
                      )}

                      {roulette.registration_status === 'closed' && !roulette.is_registered && (
                        <button type="button" className={`${styles.actionButton} ${styles.disabledAction}`} disabled>
                          <i className="bi bi-lock-fill"></i>
                          Registration closed
                        </button>
                      )}

                      {canSeeMatches(roulette) && (
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.successAction}`}
                          onClick={() => navigate(`/lunch-roulette/my-matches/${roulette.Roulette_ID}`)}
                        >
                          <i className="bi bi-people-fill"></i>
                          My matches
                        </button>
                      )}
                    </div>

                    {hasAdminRights && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.dangerAction}`}
                        onClick={() => navigateToAdminPanel(roulette.Roulette_ID)}
                      >
                        <i className="bi bi-shield-lock-fill"></i>
                        Admin console
                      </button>
                    )}
                  </footer>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>
              <i className="bi bi-calendar-x"></i>
            </span>
            <p className={styles.emptyText}>There are no lunch roulettes scheduled right now.</p>

            {hasAdminRights && (
              <button
                type="button"
                className={`${styles.actionButton} ${styles.primaryAction}`}
                data-bs-toggle="modal"
                data-bs-target="#lunchRouletteModal"
              >
                <i className="bi bi-plus-lg"></i>
                Create a lunch roulette
              </button>
            )}
          </div>
        )}
      </section>

      {showPastEvents && (
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionHeading}>Past Lunch Roulettes</h2>
              <p className={styles.sectionSubheading}>Review the events that have already taken place.</p>
            </div>
          </header>

          {data?.past && data.past.length > 0 ? (
            <div className={styles.cardGrid}>
              {data.past.map((roulette) => {
                const registrationWindow = `${formatDate(roulette.registration_start)} - ${formatDate(roulette.registration_end)}`;
                const formatIconClass = roulette.is_hybrid ? styles.iconGreen : styles.iconRed;

                return (
                  <article key={roulette.Roulette_ID} className={styles.rouletteCard} data-roulette-id={roulette.Roulette_ID}>
                    <header className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <span className={styles.cardIcon}>
                          <i className="bi bi-cup-hot-fill"></i>
                        </span>
                        <div>
                          <h3 className={styles.cardTitle}>Lunch Roulette</h3>
                          <p className={styles.cardSubline}>{registrationWindow}</p>
                        </div>
                      </div>

                      <span className={`${styles.statusPill} ${getStatusClass('completed')}`}>Completed</span>
                    </header>

                    <div className={styles.cardBody}>
                      <div className={styles.detailGrid}>
                        <div className={styles.detailRow}>
                          <span className={`${styles.detailIcon} ${styles.iconBlue}`}>
                            <i className="bi bi-people-fill"></i>
                          </span>
                          <div>
                            <p className={styles.detailLabel}>Group Size</p>
                            <p className={styles.detailValue}>{roulette.group_size} people</p>
                          </div>
                        </div>

                        <div className={styles.detailRow}>
                          <span className={`${styles.detailIcon} ${styles.iconGold}`}>
                            <i className="bi bi-calendar-event-fill"></i>
                          </span>
                          <div>
                            <p className={styles.detailLabel}>Registration Window</p>
                            <p className={styles.detailValue}>{registrationWindow}</p>
                          </div>
                        </div>

                        <div className={styles.detailRow}>
                          <span className={`${styles.detailIcon} ${formatIconClass}`}>
                            <i className={`bi ${roulette.is_hybrid ? 'bi-camera-video-fill' : 'bi-geo-alt-fill'}`}></i>
                          </span>
                          <div>
                            <p className={styles.detailLabel}>Format</p>
                            <p className={styles.detailValue}>{roulette.is_hybrid ? 'Hybrid (Microsoft Teams)' : 'In-person event'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {hasAdminRights && (
                      <section className={styles.adminInfoBox}>
                        <span className={styles.adminBadge}>Admin insights</span>
                        <div className={styles.adminGrid}>
                          <div className={styles.adminItem}>
                            <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                              <i className="bi bi-calendar-check-fill"></i>
                            </span>
                            <div>
                              <p className={styles.adminLabel}>Lunch date</p>
                              <p className={styles.adminValue}>{formatDate(roulette.lunch_date)}</p>
                            </div>
                          </div>

                          <div className={styles.adminItem}>
                            <span className={`${styles.adminIcon} ${styles.iconGreen}`}>
                              <i className="bi bi-person-check-fill"></i>
                            </span>
                            <div>
                              <p className={styles.adminLabel}>Registered users</p>
                              <p className={styles.adminValue}>{getRegisteredCount(roulette.Roulette_ID)}</p>
                            </div>
                          </div>

                          <div className={styles.adminItem}>
                            <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                              <i className="bi bi-building"></i>
                            </span>
                            <div>
                              <p className={styles.adminLabel}>Avoid same dept.</p>
                              <p className={styles.adminValue}>{roulette.not_same_department ? 'Yes' : 'No'}</p>
                            </div>
                          </div>

                          <div className={styles.adminItem}>
                            <span className={`${styles.adminIcon} ${styles.iconBlue}`}>
                              <i className="bi bi-diagram-3-fill"></i>
                            </span>
                            <div>
                              <p className={styles.adminLabel}>Avoid same group</p>
                              <p className={styles.adminValue}>{roulette.not_same_employee_group ? 'Yes' : 'No'}</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {hasAdminRights && (
                      <footer className={styles.cardActions}>
                        <div className={styles.actionGroup}></div>
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.dangerAction}`}
                          onClick={() => navigateToAdminPanel(roulette.Roulette_ID)}
                        >
                          <i className="bi bi-shield-lock-fill"></i>
                          Admin console
                        </button>
                      </footer>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>
                <i className="bi bi-journal-text"></i>
              </span>
              <p className={styles.emptyText}>No past lunch roulettes found.</p>
            </div>
          )}
        </section>
      )}

      {/* Registration Modal */}
      <div className="modal fade" id="registerModal" tabIndex={-1} aria-labelledby="registerModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className={`modal-content ${styles.modalContent}`}>
            <div className={`modal-header ${styles.modalHeader}`}>
              <div className={styles.modalHeaderGroup}>
                <span className={`${styles.modalHeaderIcon} ${styles.iconPrimary}`}>
                  <i className="bi bi-cup-hot-fill"></i>
                </span>
                <div>
                  <h5 className={styles.modalTitle}>Register for Lunch Roulette</h5>
                  <p className={styles.modalSubtitle}>Confirm your participation for this lunch event.</p>
                </div>
              </div>
              <button
                type="button"
                className={`btn-close ${styles.modalClose}`}
                onClick={() => {
                  cleanupModalBackdrops();
                  setRegistrationComment('');
                  setSelectedRouletteId(null);
                }}
                aria-label="Close"
              ></button>
            </div>

            <div className={`modal-body ${styles.modalBody}`}>
              <p className={styles.modalLead}>
                You'll be matched with team members for an informal lunch conversation across offices and departments.
              </p>

              {selectedRouletteId && data?.upcoming && (
                <div className={styles.modalInfoHighlight}>
                  <i className="bi bi-calendar-range"></i>
                  <div>
                    <strong>Registration period:</strong>
                    <span>
                      {formatDate(data.upcoming.find(r => r.Roulette_ID === selectedRouletteId)?.registration_start || '')} - {formatDate(data.upcoming.find(r => r.Roulette_ID === selectedRouletteId)?.registration_end || '')}
                    </span>
                  </div>
                </div>
              )}

              <div className={styles.modalSection}>
                <label htmlFor="comment" className={styles.fieldLabel}>
                  <span className={styles.fieldLabelIcon}>
                    <i className="bi bi-chat-dots-fill"></i>
                  </span>
                  <div>
                    <span>Matching preferences</span>
                    <small>(optional, only visible to P+C)</small>
                  </div>
                </label>
                <textarea
                  className={`form-control ${styles.fieldInput}`}
                  id="comment"
                  rows={4}
                  value={registrationComment}
                  onChange={(e) => setRegistrationComment(e.target.value)}
                  placeholder="E.g., I'd particularly enjoy meeting colleagues from [department/office], or would prefer to meet someone new..."
                />
                <div className={styles.modalHint}>
                  <span className={styles.modalHintIcon}>
                    <i className="bi bi-shield-lock-fill"></i>
                  </span>
                  <span className={styles.modalHintText}>
                    This information helps P+C create better matches and is kept confidential. You can also mention dietary preferences or conversation topics.
                  </span>
                </div>
              </div>
            </div>

            <div className={`modal-footer ${styles.modalFooter}`}>
              <button
                type="button"
                className={`${styles.modalAction} ${styles.modalSecondary}`}
                onClick={() => {
                  cleanupModalBackdrops();
                  setRegistrationComment('');
                  setSelectedRouletteId(null);
                }}
              >
                <i className="bi bi-x-lg"></i>
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.modalAction} ${styles.modalPrimary}`}
                onClick={handleRegister}
                disabled={registering}
              >
                {registering ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                    Registering...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle-fill"></i>
                    Confirm registration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Lunch Roulette Modal */}
      <div className="modal fade" id="lunchRouletteModal" tabIndex={-1} aria-labelledby="lunchRouletteModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className={`modal-content ${styles.modalContent}`}>
            <div className={`modal-header ${styles.modalHeader}`}>
              <div className={styles.modalHeaderGroup}>
                <span className={`${styles.modalHeaderIcon} ${styles.iconPrimary}`}>
                  <i className="bi bi-calendar3"></i>
                </span>
                <div>
                  <h5 className={styles.modalTitle}>Create Lunch Roulette</h5>
                  <p className={styles.modalSubtitle}>Plan the event timeline and matching preferences.</p>
                </div>
              </div>
              <button type="button" className={`btn-close ${styles.modalClose}`} data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div className={`modal-body ${styles.modalBody}`}>
              <div className={`${styles.modalCallout} ${styles.modalCalloutInfo}`}>
                <span className={styles.modalCalloutIcon}>
                  <i className="bi bi-rocket-takeoff"></i>
                </span>
                <div className={styles.modalCalloutContent}>
                  <strong>Design a memorable lunch experience</strong>
                  <p>Define registration window, group size, and optional matching rules.</p>
                </div>
              </div>

              <div className={styles.createModalSection}>
                <div className={styles.modalSectionHeader}>
                  <span className={styles.modalSectionIcon}>
                    <i className="bi bi-calendar-event-fill"></i>
                  </span>
                  <div>
                    <h6>Event details</h6>
                    <p>Set the group size and lunch delivery date.</p>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="group_size">
                      <i className="bi bi-people-fill"></i>
                      Group size
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="group_size"
                      min="2"
                      max="10"
                      value={createForm.group_size}
                      onChange={(e) => setCreateForm({ ...createForm, group_size: Number(e.target.value) })}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="lunch_date">
                      <i className="bi bi-calendar-check-fill"></i>
                      Lunch date
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="lunch_date"
                      min={createForm.registration_end || undefined}
                      value={createForm.lunch_date}
                      onChange={(e) => setCreateForm({ ...createForm, lunch_date: e.target.value })}
                    />
                    {createForm.registration_end && createForm.lunch_date &&
                      new Date(createForm.lunch_date) <= new Date(createForm.registration_end) && (
                        <div className={`${styles.validationHint} ${styles.error}`}>
                          <i className="bi bi-exclamation-circle-fill"></i>
                          <span>Lunch date must be after registration end</span>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div className={styles.createModalSection}>
                <div className={styles.modalSectionHeader}>
                  <span className={styles.modalSectionIcon}>
                    <i className="bi bi-clock-history"></i>
                  </span>
                  <div>
                    <h6>Registration period</h6>
                    <p>Open and close the registration window with clear deadlines.</p>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="registration_start">
                      <i className="bi bi-play-circle-fill"></i>
                      Registration start
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="registration_start"
                      max={createForm.registration_end || undefined}
                      value={createForm.registration_start}
                      onChange={(e) => setCreateForm({ ...createForm, registration_start: e.target.value })}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="registration_end">
                      <i className="bi bi-stop-circle-fill"></i>
                      Registration end
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="registration_end"
                      min={createForm.registration_start || undefined}
                      value={createForm.registration_end}
                      onChange={(e) => setCreateForm({ ...createForm, registration_end: e.target.value })}
                    />
                    {createForm.registration_start && createForm.registration_end &&
                      new Date(createForm.registration_end) <= new Date(createForm.registration_start) && (
                        <div className={`${styles.validationHint} ${styles.error}`}>
                          <i className="bi bi-exclamation-circle-fill"></i>
                          <span>End date must be after start date</span>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div className={styles.createModalSection}>
                <div className={styles.modalSectionHeader}>
                  <span className={styles.modalSectionIcon}>
                    <i className="bi bi-sliders"></i>
                  </span>
                  <div>
                    <h6>Event options</h6>
                    <p>Fine-tune how participants are matched.</p>
                  </div>
                </div>

                <label htmlFor="is_hybrid" className={styles.toggleSwitch}>
                  <div className={styles.toggleLabel}>
                    <i className="bi bi-camera-video-fill"></i>
                    <span>Hybrid event (via Microsoft Teams)</span>
                  </div>
                  <input
                    type="checkbox"
                    id="is_hybrid"
                    checked={createForm.is_hybrid}
                    onChange={(e) => setCreateForm({ ...createForm, is_hybrid: e.target.checked })}
                  />
                  <span className={styles.toggleSlider}></span>
                </label>

                <label htmlFor="not_same_department" className={styles.toggleSwitch}>
                  <div className={styles.toggleLabel}>
                    <i className="bi bi-building"></i>
                    <span>Avoid same department matches</span>
                  </div>
                  <input
                    type="checkbox"
                    id="not_same_department"
                    checked={createForm.not_same_department}
                    onChange={(e) => setCreateForm({ ...createForm, not_same_department: e.target.checked })}
                  />
                  <span className={styles.toggleSlider}></span>
                </label>

                <label htmlFor="not_same_employee_group" className={styles.toggleSwitch}>
                  <div className={styles.toggleLabel}>
                    <i className="bi bi-diagram-3-fill"></i>
                    <span>Avoid same employee group matches</span>
                  </div>
                  <input
                    type="checkbox"
                    id="not_same_employee_group"
                    checked={createForm.not_same_employee_group}
                    onChange={(e) => setCreateForm({ ...createForm, not_same_employee_group: e.target.checked })}
                  />
                  <span className={styles.toggleSlider}></span>
                </label>
              </div>
            </div>

            <div className={`modal-footer ${styles.modalFooter}`}>
              <button type="button" className={`${styles.modalAction} ${styles.modalSecondary}`} data-bs-dismiss="modal">
                <i className="bi bi-x-lg"></i>
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.modalAction} ${styles.modalPrimary}`}
                onClick={handleCreateRoulette}
                disabled={
                  !createForm.registration_start ||
                  !createForm.registration_end ||
                  !createForm.lunch_date ||
                  new Date(createForm.registration_end) <= new Date(createForm.registration_start) ||
                  new Date(createForm.lunch_date) <= new Date(createForm.registration_end)
                }
              >
                <i className="bi bi-check-circle-fill"></i>
                Create lunch roulette
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unregister Confirmation Modal */}
      <div className="modal fade" id="unregisterModal" tabIndex={-1} aria-labelledby="unregisterModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className={`modal-content ${styles.modalContent}`}>
            <div className={`modal-header ${styles.modalHeader}`}>
              <div className={styles.modalHeaderGroup}>
                <span className={`${styles.modalHeaderIcon} ${styles.iconPrimary}`}>
                  <i className="bi bi-question-circle-fill"></i>
                </span>
                <div>
                  <h5 className={styles.modalTitle}>Unregister from Lunch Roulette</h5>
                  <p className={styles.modalSubtitle}>Your seat will be released for another participant.</p>
                </div>
              </div>
              <button
                type="button"
                className={`btn-close ${styles.modalClose}`}
                onClick={() => {
                  cleanupModalBackdrops();
                  setUnregisterRouletteId(null);
                }}
                aria-label="Close"
              ></button>
            </div>

            <div className={`modal-body ${styles.modalBody}`}>
              <p className={styles.modalLead}>Are you sure you want to unregister from this lunch roulette?</p>
            </div>

            <div className={`modal-footer ${styles.modalFooter}`}>
              <button
                type="button"
                className={`${styles.modalAction} ${styles.modalPrimary}`}
                onClick={handleUnregister}
                disabled={unregistering}
              >
                {unregistering ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                    Unregistering...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle-fill"></i>
                    Yes, unregister
                  </>
                )}
              </button>
              <button
                type="button"
                className={`${styles.modalAction} ${styles.modalSecondary}`}
                onClick={() => {
                  cleanupModalBackdrops();
                  setUnregisterRouletteId(null);
                }}
              >
                <i className="bi bi-x-lg"></i>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LunchRouletteOverviewPage;
